import {
  Box,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import WSButton from "../../Components/Button/WSButton";

import { callAPI, formatIndianCurrency, getLocalValue, LoacalVariables, Method, toBoolean } from "../../commonFunctions/CommonFunctions";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import { NotFound } from "../NotFound";
import logo from '../../images/logo.svg'
import { IBranch } from "../DTO/Dto";
const QPrint: React.FC = () => {
  const { id } = useParams();
  const contentRef = useRef<HTMLDivElement>(null);
  const { showMessage } = useSnackbar();

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [branchData, setBranchData] = useState<IBranch>()
  const paymentStages = [
    { description: "Approximate Estimation", percentage: null },
    { description: "Advance 30%", percentage: 30 },
    { description: "Unload Time 60%", percentage: 60 },
    { description: "After Completion 10%", percentage: 10 }
  ];

  useEffect(() => {
    async function getDataById() {
      try {
        const response: any = await callAPI("estimates", Method.GET, null, id);
        if (response?.data) {
          setData(response.data);
        }
      } catch (error) {
        showMessage("Something went wrong. Please try again.", "error");
      } finally {
        setLoading(false);
      }
    }
    async function getBranchDetails() {
      try {
        const response: any = await callAPI("Branch", Method.GET, null, getLocalValue(LoacalVariables.Branch));
        if (response?.data) {
          setBranchData(response.data);
        }
      } catch (error) {
        showMessage("Something went wrong. Please try again.", "error");
      } finally {
        setLoading(false);
      }
    }
    if (id) {
      getDataById();
      getBranchDetails()
    }
  }, [id]);

  // 🖨️ Print Handler
  const handlePrint = useReactToPrint({
    contentRef
  });

  if (loading)
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  if (!data) return <NotFound />;
 
  const renderTable = (details: string[][]) => (
    <TableContainer sx={{ borderRadius: 2, boxShadow: 1 }}>
      <Table size="small">
        <TableBody>
          {details.map(([label, value], index) => (
            <TableRow
              key={index}
              sx={{ bgcolor: index % 2 ? "grey.100" : "white" }}
            >
              <TableCell sx={{ fontWeight: 500, fontSize: '0.75rem' }}> <Typography sx={{ fontSize: '0.75rem' }}>{label}:</Typography></TableCell>
              <TableCell align="right"> <Typography sx={{ fontSize: '0.75rem' }}>{value}</Typography></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
  const totalAmount = data.estimateItems
    .reduce((sum: number, item: any) => sum + Number(item.totalPrice || 0), 0)
    .toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const totalInluded = data.estimateItems
    .filter((item: any) => item.isIncluded)
    .reduce((sum: number, item: any) => sum + Number(item.unitPrice * item?.quantity), 0)
    .toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  const totalExcluded = data.estimateItems
    .filter((item: any) => !item.isIncluded)
    .reduce((sum: number, item: any) => sum + Number(item.unitPrice * item?.quantity), 0)
    .toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      p={4}
      bgcolor="grey.100"
      minHeight="100vh"
    >
      <WSButton
        id="printBtn"
        size="small"
        label="Print Estimation"
        onClick={handlePrint}
      />

      <Box
        ref={contentRef}
        mt={2}
        sx={{ p: 4, width: "100%", maxWidth: "800px", bgcolor: "white" }}
      > <Box ml={2} display={'flex'} justifyContent={'flex-end'}>
          <img src={logo} alt="Company Logo" style={{ objectFit: "contain" }} />
        </Box>
        {/* Company Header */}
        <Box textAlign="center" borderBottom={1} pb={2} mb={2}>
          <Typography variant="h4" fontWeight="bold" color="error" fontStyle={'italic'}>
            {branchData?.name}
          </Typography>
          <Typography variant="body2">
            {/* NADUKANI, PALLIVAYAL P.O 670142 */}
            {branchData?.address?.addressLine1}   {branchData?.address?.addressLine2 || ''} P.O {branchData?.address?.pincode}
          </Typography>
          <Typography variant="body2">{`PH: ${branchData?.phoneNumber},${branchData?.alternatePhoneNumber || ''}`}</Typography>
        </Box>

        {/* Estimate Details */}
        <Box textAlign="left" mb={1}>
          <Typography sx={{ fontSize: '0.875rem' }}>
            To,
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', paddingLeft: 2 }}>{data.customerName}</Typography>
          <Typography sx={{ fontSize: '0.875rem', paddingLeft: 2 }}>Site at {data.address}.</Typography>
        </Box>

        {/* Estimation Details */}
        <Box textAlign="center" mb={2} sx={{ fontSize: '0.875rem', fontWeight: 'bold' }}>
          <Typography sx={{ fontSize: '1rem', fontWeight: 'bold' }}>Estimation</Typography>
          {/* <Typography variant="body2">
            Valid Till: {new Date(data.validTill).toLocaleDateString()}
          </Typography> */}
        </Box>

        <Box mb={2}>
          <Typography sx={{ fontSize: '0.875rem' }}>Dear sir/madam</Typography>
          <Typography sx={{ fontSize: '0.875rem' }}>
            {data.subject}
          </Typography>
        </Box>

        {/* Table */}
        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 3 }}>
          <Table size="small">
            <TableHead>
              {/* Header Row */}
              <TableRow sx={{ bgcolor: 'success.main' }}>
                <TableCell sx={{ fontWeight: 700, color: 'white' }}>
                  <Typography sx={{ fontSize: '0.75rem' }} fontWeight={700}>
                    REQUIRED ITEMS
                  </Typography>
                </TableCell>
                <TableCell
                  sx={{ bgcolor: 'success.main', fontWeight: 700, color: 'white', textAlign: 'center' }}
                  colSpan={3}
                >
                  <Typography sx={{ fontSize: '0.75rem' }} fontWeight={700}>
                    WORK INCLUDED
                  </Typography>
                </TableCell>
              </TableRow>

              {/* Sub Header Row */}
              <TableRow sx={{ bgcolor: 'success.light' }}>
                <TableCell>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'white' }}>
                    PARTICULAR
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'white' }}>
                    QTY/Sqft
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'white' }}>
                    RATE
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'white' }}>
                    AMOUNT
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {data.estimateItems
                .filter((item: any) => item.isIncluded)
                .map((item: any, index: number) => (
                  <TableRow
                    key={index}
                    sx={{
                      bgcolor: index % 2 === 0 ? 'grey.50' : 'grey.100', // alternating row colors
                    }}
                  >
                    <TableCell>
                      {item.productName}{' '}
                      {item.isPolish !== '2' && (
                        <>({toBoolean(item.isPolish) ? 'With Polish' : 'Without Polish'})</>
                      )}
                    </TableCell>

                    <TableCell align="center">
                      <Typography sx={{ fontSize: '0.75rem' }}>{item.quantity}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography sx={{ fontSize: '0.75rem' }}> {Number(item.unitPrice).toFixed(2)}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography sx={{ fontSize: '0.75rem' }}> {Number(item.totalPrice).toFixed(2)}</Typography>
                    </TableCell>

                  </TableRow>
                ))}

              {/* TOTAL row */}
              <TableRow sx={{ bgcolor: 'success.lighter' }}>
                <TableCell sx={{ fontWeight: 700 }}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'success.main' }}>TOTAL</Typography>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }} />
                <TableCell align="center" sx={{ fontWeight: 700 }} />
                <TableCell align="center" sx={{ fontWeight: 700 }}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 900, color: 'success.main' }}>
                    {totalInluded
                      ? formatIndianCurrency(totalInluded)
                      : '0.00'}

                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {data.estimateItems
          .filter((item: any) => !item.isIncluded).length > 0 &&
          <TableContainer component={Paper} sx={{ marginTop: 2, borderRadius: 2, boxShadow: 3 }}>
            <Table size="small">
              <TableHead>
                {/* Main Header */}
                <TableRow sx={{ bgcolor: 'error.main' }}>
                  <TableCell sx={{ fontWeight: 700, color: 'white' }}>
                    <Typography sx={{ fontSize: '0.75rem' }} fontWeight={700}>
                      REQUIRED ITEMS
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{ bgcolor: 'error.main', fontWeight: 700, color: 'white', textAlign: 'center' }}
                    colSpan={3}
                  >
                    <Typography sx={{ fontSize: '0.75rem' }} fontWeight={700}>
                      WORK EXCLUDED
                    </Typography>
                  </TableCell>
                </TableRow>

                {/* Sub Header */}
                <TableRow sx={{ bgcolor: 'error.light' }}>
                  <TableCell>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'white' }}>
                      PARTICULAR
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'white' }}>
                      QTY/Sqft
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'white' }}>
                      RATE
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'white' }}>
                      AMOUNT
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {data.estimateItems
                  .filter((item: any) => !item.isIncluded)
                  .map((item: any, index: number) => (
                    <TableRow
                      key={index}
                      sx={{
                        bgcolor: index % 2 === 0 ? 'grey.50' : 'grey.100', // alternating row colors
                      }}
                    >
                      <TableCell>
                        <Typography sx={{ fontSize: '0.75rem' }}>{item.productName}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography sx={{ fontSize: '0.75rem' }}>{item.quantity}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography sx={{ fontSize: '0.75rem' }}> {Number(item.unitPrice).toFixed(2)}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography sx={{ fontSize: '0.75rem' }}> {Number(item.totalPrice).toFixed(2)}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}

                {/* TOTAL row */}
                <TableRow sx={{ bgcolor: 'error.lighter' }}>
                  <TableCell sx={{ fontWeight: 700 }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 900, color: 'error.main' }}>TOTAL</Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }} />
                  <TableCell align="center" sx={{ fontWeight: 700 }} />
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 900, color: 'error.main' }}>
                      {totalExcluded
                        ? formatIndianCurrency(totalExcluded)
                        : '0.00'}

                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        }
        <Typography sx={{ fontSize: '0.5rem', color: 'red', paddingTop: 1 }}>* Any changes to raw materials will affect the amount.</Typography>

        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 3, mt: 1 }}>
          <Table size="small">
            <TableHead>
              {/* Header Row */}
              <TableRow sx={{ bgcolor: '#00A300' }}>
                <TableCell sx={{ fontWeight: 700, color: 'white' }} colSpan={4}>
                  <Typography sx={{ fontSize: '0.75rem' }} fontWeight={700}>
                    Total Estimation
                  </Typography>
                </TableCell>

              </TableRow>


            </TableHead>

            <TableBody>

              <TableCell sx={{ fontWeight: 700, color: 'green', textAlign: 'right', paddingRight: 7 }} >{totalAmount
                ? formatIndianCurrency(totalAmount)
                : '0.00'}</TableCell>

              {/* TOTAL row */}
              {/* <TableRow sx={{ bgcolor: 'success.lighter' }}>
                <TableCell sx={{ fontWeight: 700 }}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'success.main' }}>TOTAL</Typography>
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }} />
                <TableCell align="center" sx={{ fontWeight: 700 }} />
                <TableCell align="center" sx={{ fontWeight: 700 }}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 900, color: 'success.main' }}>
                    {data.estimateItems
                      .filter((item: any) => !item.isIncluded)
                      .reduce((sum: number, item: any) => sum + Number(item.totalPrice), 0)
                      .toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}

                  </Typography>
                </TableCell>
              </TableRow> */}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Terms and Condtion */}
        {data.termsAndConditions !== "" && <Box mt={1}>
          <Typography sx={{ fontWeight: 700, mb: 1, fontSize: '0.75rem' }}>
            Terms and Conditions.
          </Typography>
          <Typography
            variant="body1"
            sx={{
              pl: 2,
              fontSize: '0.75rem',
              whiteSpace: 'pre-line' // 🔹 respects \n as line breaks
            }}
          >
            {data.termsAndConditions}
          </Typography>
        </Box>}
        {/* <Box mt={2}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, fontSize: '0.875rem' }}>
            Terms and Conditions.
          </Typography>
          <Typography sx={{ pl: 2, fontSize: '0.75rem' }}>
            ➤ Client should arrange the work site as per the direction the company site supervisor
          </Typography>
          <Typography sx={{ pl: 2, fontSize: '0.75rem' }}>
            ➤ Client should supply required M- Sand, Baby metal Cement, and Water for the work.
          </Typography>
          <Typography sx={{ pl: 2, fontSize: '0.75rem' }}>
            ➤ All bedding materials and any additional labor charges /Earthwork is client scope.

          </Typography>
          <Typography sx={{ pl: 2, fontSize: '0.75rem' }}>
            ➤ The work site should be free from all hindrance.
          </Typography>
          <Typography sx={{ pl: 2, fontSize: '0.75rem' }}>
            ➤ Supply of quality products and best customer service support is our prime concern
          </Typography>
          <Typography sx={{ pl: 2, fontSize: '0.75rem' }}>
            ➤ Work will start within 7 days after getting advance amount.
          </Typography>

        </Box> */}

        {/* Payment Terms */}
        {/* <Box mt={1}>
          <Typography sx={{ fontWeight: 700, mb: 0, fontSize: '0.875rem' }}>
            Mode of Payment:
          </Typography>
          <ul style={{ marginTop: 0 }}>
            <li>
              <Typography sx={{ fontSize: '0.75rem' }}>
                <b>30%</b> of total value of work on order confirmation.
              </Typography>
            </li>
            <li>
              <Typography sx={{ fontSize: '0.75rem' }}>
                <b>60%</b> of total value of work on delivery of material to
                site.
              </Typography>
            </li>
            <li>
              <Typography sx={{ fontSize: '0.75rem' }}>
                <b>10%</b> balance after completion of work.
              </Typography>
            </li>
          </ul>
        </Box> */}
        {data?.paymentTerms?.length > 0 && (
          <Box
            mt={1}
            // This prevents splitting across pages
            sx={{
              breakInside: 'avoid',         // modern
              pageBreakInside: 'avoid',     // legacy
              WebkitColumnBreakInside: 'avoid' // Safari
            }}
          >
            <Typography
              sx={{ fontWeight: 700, mb: 1, fontSize: '0.75rem' }}
            >
              Mode of Payment.
            </Typography>

            {/* Remarks */}
            {data?.paymentTerms?.map((item: any, idx: number) => (
              <Typography
                key={idx}
                variant="h6"
                sx={{ marginTop: 0, fontSize: '0.75rem', pl: 2 }}
              >
                <strong>{item?.percentage}%</strong> {item?.description}
              </Typography>
            ))}
          </Box>
        )}
        {/* Footer */}
        <Divider sx={{ my: 3 }} />
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-end"
        >
          <Box textAlign="right" sx={{ flex: 1, justifyContent: 'flex-end' }}>
            <Typography sx={{ fontSize: '0.875rem' }}>Thanks & Regards,</Typography>
            <Typography fontWeight="bold" sx={{ fontSize: '0.875rem' }}>JOSEPH PJ</Typography>
            <Typography sx={{ fontSize: '0.875rem' }}>Managing Director</Typography>
            <Typography sx={{ fontSize: '0.875rem' }}>MHB INTERLOCK</Typography>
          </Box>
        </Box>
        <Typography sx={{ fontSize: '0.5rem', color: 'red' }}>* This is an estimate; changes in some areas will also affect the amount.</Typography>

      </Box>

    </Box>
  );
};

export default QPrint;
